import { workingDaysBetween } from "../schedule/calendar.js";

/* The schedule as a workbook.
 *
 * Three sheets, because three different people ask for this file and they ask
 * for different things. The scheduler wants every activity with its logic. The
 * PM wants the milestones. Whoever is chasing the plan wants to know what is
 * driving the finish, which is the critical path on its own.
 *
 * Every column here is one the app already computes. Nothing is rounded or
 * re-derived on the way out, so a float of -3 in the file is the same -3 the
 * Schedule page is showing.
 */

const ACTIVITY_COLUMNS = [
  { key: "code", label: "Activity ID" },
  { key: "wbs", label: "WBS" },
  { key: "title", label: "Activity" },
  { key: "level", label: "Level" },
  { key: "kind", label: "Type" },
  { key: "start", label: "Start" },
  { key: "finish", label: "Finish" },
  { key: "duration", label: "Duration (days)" },
  { key: "percent", label: "% Complete" },
  { key: "float", label: "Total float (days)" },
  { key: "critical", label: "Critical" },
  { key: "baselineFinish", label: "Baseline finish" },
  { key: "variance", label: "Variance (days)" },
  { key: "predecessors", label: "Predecessors" },
  { key: "workstream", label: "Workstream" },
  { key: "notes", label: "Notes" },
];

/* Working days between two dates, signed, with the inclusive convention the
   rest of the app uses: a same-day pair is one day, so a difference is one
   less than the count. */
function drift(from, to) {
  if (!from || !to || from === to) return from && to ? 0 : null;
  const back = to < from;
  const days = workingDaysBetween(back ? to : from, back ? from : to) - 1;
  return back ? -days : days;
}

export function scheduleSheets({ activities = [], links = [], baselineRows = [] }) {
  const byId = new Map(activities.map((a) => [a.id, a]));
  const wasByCode = new Map(baselineRows.map((b) => [b.activity_code, b]));

  const predecessorsOf = new Map();
  for (const l of links) {
    const from = byId.get(l.predecessor_id);
    if (!from) continue;
    const lag = Number(l.lag_days) || 0;
    const label = `${from.activity_code ?? from.activity_id ?? "?"}`
      + `${l.link_type && l.link_type !== "FS" ? ` ${l.link_type}` : ""}`
      + `${lag ? `${lag > 0 ? "+" : ""}${lag}d` : ""}`;
    const list = predecessorsOf.get(l.successor_id) ?? [];
    list.push(label);
    predecessorsOf.set(l.successor_id, list);
  }

  const rows = activities.map((a) => {
    const start = a.early_start ?? a.start_date ?? null;
    const finish = a.early_finish ?? a.finish_date ?? null;
    const was = wasByCode.get(a.activity_code);
    return {
      code: a.activity_code ?? null,
      wbs: a.activity_id ?? null,
      title: a.title ?? null,
      level: Number.isFinite(a.outline_level) ? a.outline_level : null,
      kind: a.is_summary ? "Summary" : a.is_milestone ? "Milestone" : "Task",
      start,
      finish,
      duration: Number.isFinite(a.duration_days) ? a.duration_days : null,
      percent: Number.isFinite(a.percent_complete) ? a.percent_complete : null,
      /* A summary has no float of its own; it inherits from what is under it,
         and printing a number there invites somebody to act on it. */
      float: a.is_summary ? null : (Number.isFinite(a.total_float) ? a.total_float : null),
      critical: a.is_summary ? null : !!a.is_critical,
      baselineFinish: was?.finish_date ?? null,
      variance: was?.finish_date ? drift(was.finish_date, finish) : null,
      predecessors: (predecessorsOf.get(a.id) ?? []).join(", ") || null,
      workstream: a.workstream ?? null,
      notes: a.notes ?? null,
    };
  });

  const milestones = rows.filter((r) => r.kind === "Milestone");
  const critical = rows.filter((r) => r.critical === true);

  return [
    { title: "Activities", columns: ACTIVITY_COLUMNS, rows },
    { title: "Milestones", columns: ACTIVITY_COLUMNS, rows: milestones },
    { title: "Critical path", columns: ACTIVITY_COLUMNS, rows: critical },
  ];
}
